/**
 * Error-shape helpers for the Apollo adapter.
 *
 * PDGS answers some reads with a 404 that is an expected *state* rather than
 * a failure — `pdgIngestionValidation` for an ingestion whose validator has
 * not run yet, for example. The BFF surfaces that as a GraphQL error, so the
 * adapter has to recognise it and map it to an empty domain result; letting
 * it propagate would show operators an error panel for a perfectly healthy
 * in-flight ingestion.
 *
 * Kept here rather than in a hook or component because "what a 404 looks
 * like on the wire" is transport knowledge, which the port exists to hide.
 */

const NOT_FOUND_STATUS = 404;
const NOT_FOUND_CODE = 'NOT_FOUND';

interface StatusCarrier {
  status?: unknown;
  statusCode?: unknown;
}

interface GraphQLErrorLike {
  extensions?: Record<string, unknown> | null;
}

interface ApolloErrorLike {
  graphQLErrors?: readonly GraphQLErrorLike[];
  networkError?: unknown;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Apollo hangs the HTTP status off different keys depending on the layer. */
const hasNotFoundStatus = (value: unknown): boolean => {
  if (!isObject(value)) {
    return false;
  }

  const { status, statusCode } = value as StatusCarrier;

  return status === NOT_FOUND_STATUS || statusCode === NOT_FOUND_STATUS;
};

const isNotFoundExtension = (
  extensions: Record<string, unknown> | null | undefined,
): boolean => {
  if (!extensions) {
    return false;
  }

  return (
    extensions.code === NOT_FOUND_CODE ||
    hasNotFoundStatus(extensions) ||
    hasNotFoundStatus(extensions.response) ||
    hasNotFoundStatus(extensions.exception)
  );
};

/**
 * Whether an error thrown by Apollo represents an upstream 404.
 *
 * Duck-typed rather than `instanceof ApolloError` so it also holds for the
 * plain error objects a non-Apollo link (or a test double) can surface.
 */
export const isNotFoundError = (error: unknown): boolean => {
  if (!isObject(error)) {
    return false;
  }

  const { graphQLErrors = [], networkError } = error as ApolloErrorLike;

  return (
    hasNotFoundStatus(networkError) ||
    graphQLErrors.some(graphQLError =>
      isNotFoundExtension(graphQLError?.extensions),
    )
  );
};
